/**
 * Rich documents for announcements (#22) and knowledge base articles (#23).
 *
 * Content is stored as TipTap/ProseMirror JSON. HTML is rendered here, from a whitelist of node and mark types,
 * so stored HTML can never carry script, event handlers, styles or arbitrary iframes: unknown nodes are dropped
 * (their text kept), links must be http(s)/mailto/relative, images must be uploaded attachments or https, and
 * video embeds are rebuilt from a recognised YouTube / Vimeo / Microsoft Stream URL.
 */
export interface RichMark { type: string; attrs?: Record<string, unknown> }
export interface RichNode { type: string; attrs?: Record<string, unknown>; content?: RichNode[]; text?: string; marks?: RichMark[] }
export type RichDoc = RichNode & { type: 'doc' };

export const CALLOUT_TONES = ['info', 'success', 'warning', 'danger'] as const;
export type CalloutTone = typeof CALLOUT_TONES[number];

const MAX_DEPTH = 40;
const MAX_NODES = 20_000;

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s: unknown): s is string => typeof s === 'string' && UUID.test(s);

/** Safe href or null: http(s), mailto, or an app-relative path. */
export function safeHref(href: unknown): string | null {
  if (typeof href !== 'string') return null;
  const h = href.trim();
  if (h.length === 0 || h.length > 2000) return null;
  if (/^\/(?!\/)/.test(h) || /^#[\w-]*$/.test(h)) return h;
  try {
    const u = new URL(h);
    return ['http:', 'https:', 'mailto:'].includes(u.protocol) ? u.toString() : null;
  } catch { return null; }
}

/** A recognised video page URL turned into its embed URL, or null. */
export function toEmbedUrl(url: unknown): { provider: 'youtube' | 'vimeo' | 'stream'; src: string } | null {
  if (typeof url !== 'string') return null;
  let u: URL;
  try { u = new URL(url.trim()); } catch { return null; }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.replace(/^www\.|^m\./, '');
  const id = (s: string | null | undefined) => (s && /^[\w-]{6,64}$/.test(s) ? s : null);
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const v = id(u.searchParams.get('v')) ?? id(u.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]+)/)?.[1]);
    return v ? { provider: 'youtube', src: `https://www.youtube-nocookie.com/embed/${v}` } : null;
  }
  if (host === 'youtu.be') { const v = id(u.pathname.slice(1)); return v ? { provider: 'youtube', src: `https://www.youtube-nocookie.com/embed/${v}` } : null; }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const v = u.pathname.match(/(?:^|\/)(\d{5,12})(?:\/|$)/)?.[1];
    return v ? { provider: 'vimeo', src: `https://player.vimeo.com/video/${v}` } : null;
  }
  // Microsoft Stream (on SharePoint) and classic Stream share links.
  if (/(^|\.)sharepoint\.com$/.test(host) && /\.(mp4|mov|webm)$|\/stream\.aspx/i.test(u.pathname + u.search)) {
    const embed = new URL(u.toString()); embed.searchParams.set('embed', '1'); embed.hash = '';
    return { provider: 'stream', src: embed.toString() };
  }
  if (host === 'web.microsoftstream.com') {
    const v = u.pathname.match(/\/video\/([0-9a-f-]{36})/i)?.[1];
    return v ? { provider: 'stream', src: `https://web.microsoftstream.com/embed/video/${v}` } : null;
  }
  return null;
}

/** Attachment ids referenced by image and video nodes (so the API can check they belong to the workspace). */
export function docAttachmentIds(doc: RichNode | null | undefined): string[] {
  const out = new Set<string>();
  walk(doc, n => {
    const a = n.attrs?.attachmentId;
    if ((n.type === 'image' || n.type === 'video' || n.type === 'file') && isUuid(a)) out.add(a.toLowerCase());
  });
  return [...out];
}

function walk(n: RichNode | null | undefined, f: (n: RichNode) => void, depth = 0) {
  if (!n || typeof n !== 'object' || depth > MAX_DEPTH) return;
  f(n);
  if (Array.isArray(n.content)) for (const c of n.content) walk(c, f, depth + 1);
}

/**
 * Plain text (for search, diffs and change detection). Blocks end with a newline. Images, videos and files become
 * `[image: alt]` / `[video: title]` / `[file: name]` placeholders, so the stored text (search, version hashes, the
 * assistant) still knows they are there. Previews pass `{ media: false }` to leave them out (t172u).
 */
export function docText(doc: RichNode | null | undefined, opts: { media?: boolean } = {}): string {
  const media = opts.media !== false;
  const out: string[] = [];
  const block = new Set(['paragraph', 'heading', 'listItem', 'blockquote', 'codeBlock', 'tableRow', 'callout', 'horizontalRule']);
  const rec = (n: RichNode, depth: number) => {
    if (!n || depth > MAX_DEPTH) return;
    if (n.type === 'text' && typeof n.text === 'string') out.push(n.text);
    else if (n.type === 'hardBreak') out.push('\n');
    else if (n.type === 'tableCell' || n.type === 'tableHeader') { (n.content ?? []).forEach(c => rec(c, depth + 1)); out.push(' | '); return; }
    else if (!media && (n.type === 'image' || n.type === 'video' || n.type === 'embed' || n.type === 'file')) { /* left out of previews */ }
    else if (n.type === 'image') out.push(`[image${typeof n.attrs?.alt === 'string' && n.attrs.alt ? `: ${n.attrs.alt}` : ''}]`);
    else if (n.type === 'video' || n.type === 'embed') out.push(`[video${typeof n.attrs?.title === 'string' && n.attrs.title ? `: ${n.attrs.title}` : ''}]`);
    else if (n.type === 'file') out.push(`[file${typeof n.attrs?.name === 'string' ? `: ${n.attrs.name}` : ''}]`);
    (n.content ?? []).forEach(c => rec(c, depth + 1));
    if (block.has(n.type)) out.push('\n');
  };
  if (doc) rec(doc, 0);
  return out.join('').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Preview text: docText without media placeholders, spaces tidied (card excerpts, notification bodies). */
export const docPreviewText = (doc: RichNode | null | undefined): string => docText(doc, { media: false }).replace(/[ \t]{2,}/g, ' ');

/**
 * Remove `[image]`, `[image: alt]`, `[video: …]` and `[file: …]` placeholders from text that was stored with them
 * (body_text, ts_headline snippets, where hit marks «» may sit inside one). For previews and search snippets (t172u).
 */
export function stripMediaPlaceholders(text: string): string;
export function stripMediaPlaceholders(text: string | null | undefined): string | null | undefined;
export function stripMediaPlaceholders(text: string | null | undefined) {
  if (!text) return text;
  return text.replace(/\[(?:image|video|file)(?::[^\]\n]*)?\]/g, '').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export interface DocImage { attachmentId: string | null; src: string | null; alt: string; width: number | null; height: number | null }

/** Image nodes in document order: uploaded attachments (attachmentId) or https images (src). Others are skipped, as renderDoc does. */
export function docImages(doc: RichNode | null | undefined): DocImage[] {
  const out: DocImage[] = [];
  const dim = (v: unknown) => (Number.isInteger(v) && (v as number) > 0 && (v as number) <= 100000 ? v as number : null);
  walk(doc, n => {
    if (n.type !== 'image') return;
    const alt = typeof n.attrs?.alt === 'string' ? n.attrs.alt.slice(0, 300) : '';
    const w = dim(n.attrs?.width), h = dim(n.attrs?.height);
    if (isUuid(n.attrs?.attachmentId)) out.push({ attachmentId: (n.attrs!.attachmentId as string).toLowerCase(), src: null, alt, width: w, height: h });
    else { const src = safeHref(n.attrs?.src); if (src && src.startsWith('https://')) out.push({ attachmentId: null, src, alt, width: w, height: h }); }
  });
  return out;
}

/** Headings for a table of contents, with the anchors renderDoc gives them. */
export function docHeadings(doc: RichNode | null | undefined): { level: number; text: string; id: string }[] {
  const out: { level: number; text: string; id: string }[] = [];
  const used = new Map<string, number>();
  walk(doc, n => {
    if (n.type !== 'heading') return;
    const text = docText({ type: 'doc', content: n.content });
    out.push({ level: clampLevel(n.attrs?.level), text, id: anchor(text, used) });
  });
  return out;
}
const clampLevel = (l: unknown) => Math.min(Math.max(Number(l) || 2, 1), 4);
function anchor(text: string, used: Map<string, number>): string {
  const base = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section';
  const n = used.get(base) ?? 0; used.set(base, n + 1);
  return n ? `${base}-${n + 1}` : base;
}

function renderMarks(text: string, marks: RichMark[] | undefined): string {
  let html = escapeHtml(text);
  for (const m of marks ?? []) {
    switch (m.type) {
      case 'bold': html = `<strong>${html}</strong>`; break;
      case 'italic': html = `<em>${html}</em>`; break;
      case 'strike': html = `<s>${html}</s>`; break;
      case 'underline': html = `<u>${html}</u>`; break;
      case 'code': html = `<code>${html}</code>`; break;
      case 'link': {
        const href = safeHref(m.attrs?.href);
        if (href) html = `<a href="${escapeHtml(href)}"${href.startsWith('/') || href.startsWith('#') ? '' : ' target="_blank" rel="noopener noreferrer nofollow"'}>${html}</a>`;
        break;
      }
      default: break; // unknown marks dropped
    }
  }
  return html;
}

/** Sanitised HTML for a document. Attachment media render as data-attachment-id placeholders the client resolves. */
export function renderDoc(doc: RichNode | null | undefined): string {
  let count = 0;
  const used = new Map<string, number>();
  const kids = (n: RichNode, d: number) => (Array.isArray(n.content) ? n.content.map(c => rec(c, d + 1)).join('') : '');
  const rec = (n: RichNode, d: number): string => {
    if (!n || typeof n !== 'object' || d > MAX_DEPTH || ++count > MAX_NODES) return '';
    switch (n.type) {
      case 'doc': return kids(n, d);
      case 'text': return typeof n.text === 'string' ? renderMarks(n.text, n.marks) : '';
      case 'paragraph': return `<p>${kids(n, d)}</p>`;
      case 'heading': {
        const l = clampLevel(n.attrs?.level);
        const id = anchor(docText({ type: 'doc', content: n.content }), used);
        return `<h${l} id="${id}">${kids(n, d)}</h${l}>`;
      }
      case 'bulletList': return `<ul>${kids(n, d)}</ul>`;
      case 'orderedList': {
        const start = Number(n.attrs?.start);
        return `<ol${Number.isInteger(start) && start > 1 && start < 100000 ? ` start="${start}"` : ''}>${kids(n, d)}</ol>`;
      }
      case 'listItem': return `<li>${kids(n, d)}</li>`;
      case 'blockquote': return `<blockquote>${kids(n, d)}</blockquote>`;
      case 'codeBlock': return `<pre><code>${escapeHtml(docText({ type: 'doc', content: n.content }))}</code></pre>`;
      case 'hardBreak': return '<br>';
      case 'horizontalRule': return '<hr>';
      case 'table': return `<div class="rt-table"><table><tbody>${kids(n, d)}</tbody></table></div>`;
      case 'tableRow': return `<tr>${kids(n, d)}</tr>`;
      case 'tableHeader': case 'tableCell': {
        const tag = n.type === 'tableHeader' ? 'th' : 'td';
        const span = (k: string) => { const v = Number(n.attrs?.[k]); return Number.isInteger(v) && v > 1 && v <= 50 ? ` ${k}="${v}"` : ''; };
        return `<${tag}${span('colspan')}${span('rowspan')}>${kids(n, d)}</${tag}>`;
      }
      case 'callout': {
        const tone = (CALLOUT_TONES as readonly string[]).includes(String(n.attrs?.tone)) ? String(n.attrs?.tone) : 'info';
        return `<div class="rt-callout rt-callout-${tone}" data-tone="${tone}">${kids(n, d)}</div>`;
      }
      case 'image': {
        const alt = escapeHtml(typeof n.attrs?.alt === 'string' ? n.attrs.alt.slice(0, 300) : '');
        if (isUuid(n.attrs?.attachmentId)) {
          // Feedback 1 #7: intrinsic size (measured at upload) reserves the box, so nothing jumps when the image loads.
          const dim = (v: unknown) => (Number.isInteger(v) && (v as number) > 0 && (v as number) <= 100000 ? v as number : null);
          const w = dim(n.attrs!.width), h = dim(n.attrs!.height);
          return `<img data-attachment-id="${n.attrs!.attachmentId}" alt="${alt}"${w && h ? ` width="${w}" height="${h}"` : ''} loading="lazy" decoding="async">`;
        }
        const src = safeHref(n.attrs?.src);
        return src && src.startsWith('https://') ? `<img src="${escapeHtml(src)}" alt="${alt}" loading="lazy" referrerpolicy="no-referrer">` : '';
      }
      case 'video': {
        if (!isUuid(n.attrs?.attachmentId)) return '';
        const title = escapeHtml(typeof n.attrs?.title === 'string' ? n.attrs.title.slice(0, 200) : 'Video');
        return `<video data-attachment-id="${n.attrs!.attachmentId}" controls preload="metadata" title="${title}"></video>`;
      }
      case 'embed': {
        const e = toEmbedUrl(n.attrs?.url);
        if (!e) return '';
        const title = escapeHtml(typeof n.attrs?.title === 'string' ? n.attrs.title.slice(0, 200) : `${e.provider} video`);
        return `<div class="rt-embed" data-provider="${e.provider}"><iframe src="${escapeHtml(e.src)}" title="${title}" loading="lazy" allowfullscreen `
          + `sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`;
      }
      case 'file': {
        if (!isUuid(n.attrs?.attachmentId)) return '';
        const name = escapeHtml(typeof n.attrs?.name === 'string' ? n.attrs.name.slice(0, 200) : 'Attachment');
        return `<a class="rt-file" data-attachment-id="${n.attrs!.attachmentId}" href="#">${name}</a>`;
      }
      default: return kids(n, d); // unknown node: keep its children (text), drop the wrapper
    }
  };
  return doc ? rec(doc, 0) : '';
}

/** A doc made of plain paragraphs (seed data, templates, tests). */
export function docFromText(text: string): RichDoc {
  return { type: 'doc', content: text.split(/\n{2,}/).map(p => ({ type: 'paragraph', content: p ? [{ type: 'text', text: p }] : [] })) };
}

/** Line diff (LCS) of two texts; for version history. */
export function lineDiff(a: string, b: string): { op: 'same' | 'add' | 'del'; text: string }[] {
  const x = a.split('\n'), y = b.split('\n');
  if (x.length * y.length > 4_000_000) return [...x.map(text => ({ op: 'del' as const, text })), ...y.map(text => ({ op: 'add' as const, text }))];
  const m = x.length, n = y.length;
  const dp: Uint32Array[] = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: { op: 'same' | 'add' | 'del'; text: string }[] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) { out.push({ op: 'same', text: x[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ op: 'del', text: x[i++] });
    else out.push({ op: 'add', text: y[j++] });
  }
  while (i < m) out.push({ op: 'del', text: x[i++] });
  while (j < n) out.push({ op: 'add', text: y[j++] });
  return out;
}

/**
 * Tidy a Postgres ts_headline snippet (hits marked «»): keep only the blocks (lines) with hits, start each at a
 * sentence boundary where one precedes the first hit, and mark cut-offs with an ellipsis.
 */
export function tidySnippet(raw: string | null | undefined): string | null {
  if (!raw) return null;
  raw = stripMediaPlaceholders(raw);
  if (!raw) return null;
  const blocks = raw.split(/[\n¶]+/).map(b => b.trim()).filter(Boolean);
  const hits = blocks.filter(b => b.includes('«'));
  const keep = (hits.length ? hits : blocks.slice(0, 1)).slice(0, 2);
  const out = keep.map(b => {
    let s = b;
    const first = s.indexOf('«');
    if (/^[a-z«]/.test(s)) {
      const before = first > 0 ? s.slice(0, first) : '';
      const m = [...before.matchAll(/[.!?;:]\s+(?=\S)/g)].pop();
      s = m ? s.slice(m.index! + m[0].length) : `… ${s}`;
    }
    if (!/[.!?:)»]$/.test(s)) s = `${s} …`;
    return s;
  });
  return out.join(' … ').replace(/… …/g, '…');
}
